import React, { useRef, useState, useEffect, useCallback } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useSpring,
  useMotionValue,
  useReducedMotion,
} from "framer-motion";
import { projects, projectsHeader } from "../portfolio";
import { blurIn } from "../utils/motion";
import {
  FiArrowUpRight,
  FiExternalLink,
  FiPlay,
  FiCheck,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";
import { FaGithub } from "react-icons/fa";
import ScrambleText from "./ScrambleText";
import { playCardHover, playHover, playClick } from "../utils/soundEngine";

const PROJECT_CONFIGS = [
  {
    roman: "I",
    category: "3D Retro Workstation",
    liveLabel: "Launch Workstation",
    pulseColor: "bg-emerald-400",
  },
  {
    roman: "II",
    category: "3D Cyberpunk Game",
    liveLabel: "Play Game",
    pulseColor: "bg-purple-400",
  },
  {
    roman: "III",
    category: "3D Aviation Engine",
    liveLabel: "Launch Experience",
    pulseColor: "bg-amber-400",
  },
  {
    roman: "IV",
    category: "Tactical Database OS",
    liveLabel: "Open Database",
    pulseColor: "bg-emerald-400",
  },
  {
    roman: "V",
    category: "Local Vector RAG",
    liveLabel: "View Architecture",
    pulseColor: "bg-purple-400",
  },
  {
    roman: "VI",
    category: "Cybersecurity GNN",
    liveLabel: "Explore Platform",
    pulseColor: "bg-sky-400",
  },
];

function DesktopProjectCard({ project, index, total, scrollYProgress }) {
  const cardRef = useRef(null);
  const primaryUrl = project.url || project.github;
  const config = PROJECT_CONFIGS[index] || PROJECT_CONFIGS[0];
  const shouldReduce = useReducedMotion();

  const imagePath = (() => {
    try {
      return require(`../assests/images/${project.image}`);
    } catch {
      return null;
    }
  })();

  // Continuous function mapping for smooth scaling & opacity
  const cardScale = useTransform(scrollYProgress, (progress) => {
    if (shouldReduce) return 1;
    const currentIdx = progress * (total - 1);
    const dist = Math.abs(currentIdx - index);
    const factor = Math.max(0, 1 - dist * 0.95);
    return 0.88 + factor * 0.14; // 0.88x -> 1.02x
  });

  const cardOpacity = useTransform(scrollYProgress, (progress) => {
    if (shouldReduce) return 1;
    const currentIdx = progress * (total - 1);
    const dist = Math.abs(currentIdx - index);
    const factor = Math.max(0, 1 - dist * 0.9);
    return 0.55 + factor * 0.45; // 0.55 -> 1.0
  });

  // Parallax shift for screenshot image inside the viewport frame
  const imageParallaxX = useTransform(scrollYProgress, (progress) => {
    if (shouldReduce) return 0;
    const currentIdx = progress * (total - 1);
    const diff = currentIdx - index;
    const clamped = Math.max(-1, Math.min(1, diff));
    return clamped * -18; // -18px to +18px
  });

  // 3D Tilt Spring Physics on Mouse Hover (Tightly dampened)
  const mouseX = useMotionValue(0.5);
  const mouseY = useMotionValue(0.5);

  const rotateX = useSpring(
    useTransform(mouseY, [0, 1], shouldReduce ? [0, 0] : [1.8, -1.8]),
    {
      stiffness: 320,
      damping: 32,
      mass: 0.7,
    }
  );
  const rotateY = useSpring(
    useTransform(mouseX, [0, 1], shouldReduce ? [0, 0] : [-1.8, 1.8]),
    {
      stiffness: 320,
      damping: 32,
      mass: 0.7,
    }
  );

  const spotlightX = useTransform(mouseX, [0, 1], ["0%", "100%"]);
  const spotlightY = useTransform(mouseY, [0, 1], ["0%", "100%"]);

  const handleMouseMove = useCallback(
    (e) => {
      if (!cardRef.current || shouldReduce) return;
      const rect = cardRef.current.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = (e.clientY - rect.top) / rect.height;
      mouseX.set(x);
      mouseY.set(y);
    },
    [mouseX, mouseY, shouldReduce]
  );

  const handleMouseLeave = useCallback(() => {
    mouseX.set(0.5);
    mouseY.set(0.5);
  }, [mouseX, mouseY]);

  const displayHost = (() => {
    try {
      const u = new URL(primaryUrl);
      return u.hostname.replace(/^www\./, "");
    } catch {
      return "project.app";
    }
  })();

  return (
    <motion.div
      style={{
        scale: cardScale,
        opacity: cardOpacity,
        perspective: 1200,
      }}
      className="w-[740px] lg:w-[820px] xl:w-[890px] shrink-0 h-full flex flex-col justify-center transition-opacity duration-300"
    >
      <motion.div
        ref={cardRef}
        onMouseMove={handleMouseMove}
        onMouseEnter={() => playCardHover()}
        onMouseLeave={handleMouseLeave}
        style={{
          rotateX,
          rotateY,
          transformStyle: "preserve-3d",
        }}
        className="group relative flex flex-col justify-between rounded-3xl border border-surface/[0.1] hover:border-surface/[0.28] bg-surface/[0.025] hover:bg-surface/[0.045] p-6 lg:p-8 transition-colors duration-400 ease-out-expo overflow-hidden shadow-[0_16px_48px_rgba(0,0,0,0.22)]"
      >
        {/* Specular Cursor Spotlight Glow */}
        <motion.div
          className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          style={{
            background: `radial-gradient(750px circle at ${spotlightX} ${spotlightY}, rgba(var(--color-surface) / 0.06), transparent 70%)`,
          }}
        />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-center">
          {/* Left: Viewport Image Frame with Parallax Image */}
          <div className="lg:col-span-7">
            <div className="relative rounded-2xl overflow-hidden border border-surface/[0.1] bg-surface/[0.04] shadow-inner">
              {/* Window Header Bar */}
              <div className="flex items-center justify-between px-3.5 py-2.5 bg-surface/[0.03] border-b border-surface/[0.06]">
                <div className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-surface/[0.2] group-hover:bg-red-500/70 transition-colors duration-300" />
                  <span className="w-2.5 h-2.5 rounded-full bg-surface/[0.2] group-hover:bg-amber-500/70 transition-colors duration-300" />
                  <span className="w-2.5 h-2.5 rounded-full bg-surface/[0.2] group-hover:bg-emerald-500/70 transition-colors duration-300" />
                </div>
                <span className="font-mono text-[11px] text-fg-faint truncate max-w-[220px] px-2.5 py-0.5 rounded-md bg-surface/[0.04] border border-surface/[0.05]">
                  {displayHost}
                </span>
                <a
                  href={primaryUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Open preview of ${project.name}`}
                  className="text-fg-faint hover:text-fg transition-colors"
                >
                  <FiExternalLink size={13} />
                </a>
              </div>

              {/* Viewport Image Asset with Parallax */}
              <a
                href={primaryUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block relative aspect-[16/10] overflow-hidden bg-surface/[0.02]"
              >
                {imagePath && (
                  <motion.img
                    src={imagePath}
                    alt={`${project.name} preview`}
                    style={{ x: imageParallaxX }}
                    loading="lazy"
                    className="w-full h-full object-cover object-top scale-[1.06] group-hover:scale-[1.1] transition-transform duration-700 ease-out-expo"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-transparent opacity-60 group-hover:opacity-20 transition-opacity duration-300" />

                {/* Floating Interactive Badge */}
                <div className="absolute bottom-3 left-3 sm:bottom-4 sm:left-4 z-10 flex items-center gap-2 px-3 py-1.5 rounded-lg bg-bg-raised/90 dark:bg-black/85 backdrop-blur-md border border-surface/[0.12] shadow-lg text-[11px] font-mono text-fg">
                  <span
                    className={`w-2 h-2 rounded-full ${config.pulseColor} animate-pulse shadow-[0_0_8px_currentColor]`}
                  />
                  <span>{config.category}</span>
                </div>
              </a>
            </div>
          </div>

          {/* Right: Meta & Highlights */}
          <div className="lg:col-span-5 flex flex-col justify-between h-full space-y-4">
            <div>
              {/* Header Badge & Project Counter */}
              <div className="flex items-center justify-between gap-3 mb-2">
                <div className="inline-flex items-center gap-2 px-2.5 py-0.5 rounded-full bg-surface/[0.06] border border-surface/[0.12] text-fg text-[10.5px] font-mono tracking-wider uppercase font-semibold">
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${config.pulseColor} animate-ping`}
                  />
                  {config.category}
                </div>
                <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-fg-faint font-medium">
                  {String(index + 1).padStart(2, "0")} /{" "}
                  {String(total).padStart(2, "0")}
                </span>
              </div>

              {/* Title & Tagline */}
              <h3 className="font-display text-2xl lg:text-3xl font-bold text-fg mb-1 tracking-tight group-hover:text-fg transition-colors duration-300">
                {project.name}
              </h3>
              {project.tagline && (
                <p className="font-mono text-xs text-fg-faint mb-2.5 tracking-wide">
                  {project.tagline}
                </p>
              )}

              {/* Description */}
              <p className="text-xs sm:text-[13px] text-fg-muted leading-relaxed mb-3 line-clamp-3 lg:line-clamp-4">
                {project.description}
              </p>

              {/* Highlights */}
              {project.highlights && (
                <div className="space-y-1.5 pt-2.5 border-t border-surface/[0.06] mb-3">
                  {project.highlights.slice(0, 2).map((highlight, idx) => (
                    <div
                      key={idx}
                      className="flex items-start gap-2 text-[11.5px] text-fg-muted font-sans"
                    >
                      <FiCheck
                        size={13}
                        className="text-fg mt-0.5 shrink-0 opacity-80"
                      />
                      <span className="leading-snug">{highlight}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Tech Stack & Action Links */}
            <div className="pt-3 border-t border-surface/[0.06] space-y-3.5">
              <div className="flex flex-wrap gap-1.5">
                {project.languages &&
                  project.languages.map((lang) => (
                    <span
                      key={lang.name}
                      className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-surface/[0.03] border border-surface/[0.06] text-[11px] font-mono text-fg-faint group-hover:border-surface/[0.12] transition-colors"
                    >
                      <span
                        className="iconify text-xs opacity-75"
                        data-icon={lang.iconifyClass}
                      />
                      {lang.name}
                    </span>
                  ))}
              </div>

              <div className="flex items-center gap-3 pt-1">
                {project.url && (
                  <a
                    href={project.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onMouseEnter={() => playHover()}
                    onClick={() => playClick()}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-fg text-bg font-sans text-xs sm:text-sm font-semibold hover:opacity-90 transition-all duration-300 shadow-md hover:shadow-lg group-hover:scale-[1.01]"
                  >
                    <FiPlay size={12} className="fill-current" />
                    {config.liveLabel}
                    <FiArrowUpRight size={14} />
                  </a>
                )}
                {project.github && (
                  <a
                    href={project.github}
                    target="_blank"
                    rel="noopener noreferrer"
                    onMouseEnter={() => playHover()}
                    onClick={() => playClick()}
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-surface/[0.04] hover:bg-surface/[0.08] border border-surface/[0.08] text-xs sm:text-sm font-medium text-fg-muted hover:text-fg transition-all duration-300"
                  >
                    <FaGithub size={14} />
                    Source
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

function MobileProjectCard({ project, index, total }) {
  const primaryUrl = project.url || project.github;
  const config = PROJECT_CONFIGS[index] || PROJECT_CONFIGS[0];

  const imagePath = (() => {
    try {
      return require(`../assests/images/${project.image}`);
    } catch {
      return null;
    }
  })();

  const displayHost = (() => {
    try {
      const u = new URL(primaryUrl);
      return u.hostname.replace(/^www\./, "");
    } catch {
      return "project.app";
    }
  })();

  return (
    <div className="w-[86vw] sm:w-[420px] shrink-0 snap-center rounded-2xl border border-surface/[0.1] bg-surface/[0.025] p-5 shadow-lg flex flex-col justify-between">
      <div>
        {/* Header Preview Image */}
        <div className="relative rounded-xl overflow-hidden border border-surface/[0.1] bg-surface/[0.04] mb-4">
          <div className="flex items-center justify-between px-3 py-2 bg-surface/[0.03] border-b border-surface/[0.06]">
            <div className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-surface/[0.2]" />
              <span className="w-2 h-2 rounded-full bg-surface/[0.2]" />
              <span className="w-2 h-2 rounded-full bg-surface/[0.2]" />
            </div>
            <span className="font-mono text-[10px] text-fg-faint truncate max-w-[180px]">
              {displayHost}
            </span>
            <a
              href={primaryUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Open project"
              className="text-fg-faint"
            >
              <FiExternalLink size={11} />
            </a>
          </div>

          <a
            href={primaryUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="block relative aspect-[16/10] overflow-hidden bg-surface/[0.02]"
          >
            {imagePath && (
              <img
                src={imagePath}
                alt={`${project.name} preview`}
                loading="lazy"
                className="w-full h-full object-cover object-top"
              />
            )}
          </a>
        </div>

        {/* Meta */}
        <div className="flex items-center justify-between gap-2 mb-1.5">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-surface/[0.06] border border-surface/[0.1] text-fg text-[10px] font-mono uppercase font-semibold">
            {config.category}
          </span>
          <span className="font-mono text-[10.5px] text-fg-faint">
            {String(index + 1).padStart(2, "0")} /{" "}
            {String(total).padStart(2, "0")}
          </span>
        </div>

        <h3 className="font-display text-lg font-bold text-fg mb-1">
          {project.name}
        </h3>
        <p className="text-xs text-fg-muted leading-relaxed line-clamp-3 mb-3">
          {project.description}
        </p>
      </div>

      {/* Footer */}
      <div className="pt-3 border-t border-surface/[0.06] space-y-3">
        <div className="flex flex-wrap gap-1">
          {project.languages &&
            project.languages.slice(0, 4).map((lang) => (
              <span
                key={lang.name}
                className="px-2 py-0.5 rounded bg-surface/[0.03] border border-surface/[0.06] text-[10px] font-mono text-fg-faint"
              >
                {lang.name}
              </span>
            ))}
        </div>

        <div className="flex items-center gap-2.5">
          {project.url && (
            <a
              href={project.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-fg text-bg text-xs font-semibold"
            >
              <FiPlay size={11} className="fill-current" />
              {config.liveLabel}
              <FiArrowUpRight size={12} />
            </a>
          )}
          {project.github && (
            <a
              href={project.github}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-surface/[0.04] border border-surface/[0.08] text-xs font-medium text-fg-muted"
            >
              <FaGithub size={12} />
              Code
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

function Projects() {
  const containerRef = useRef(null);
  const trackRef = useRef(null);
  const mobileTrackRef = useRef(null);
  const totalProjects = projects.data.length;
  const [scrollDistance, setScrollDistance] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);

  // Exact measurement of travel distance
  const calculateDistance = useCallback(() => {
    if (!trackRef.current) return;
    const trackWidth = trackRef.current.scrollWidth;
    const viewportWidth = window.innerWidth;
    const distance = Math.max(0, trackWidth - viewportWidth + 80);
    setScrollDistance(distance);
  }, []);

  useEffect(() => {
    calculateDistance();
    window.addEventListener("resize", calculateDistance);
    return () => window.removeEventListener("resize", calculateDistance);
  }, [calculateDistance]);

  // Track overall page scroll progress
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  // Transform vertical scroll progress into exact pixel horizontal translation
  const x = useTransform(scrollYProgress, [0, 1], [0, -scrollDistance]);

  const smoothX = useSpring(x, {
    stiffness: 170,
    damping: 26,
    restDelta: 0.5,
  });

  const scaleX = useSpring(scrollYProgress, {
    stiffness: 220,
    damping: 28,
    restDelta: 0.001,
  });

  // Track active center index
  useEffect(() => {
    return scrollYProgress.on("change", (latest) => {
      const idx = Math.min(
        Math.floor(latest * totalProjects + 0.15),
        totalProjects - 1
      );
      setActiveIndex(idx);
    });
  }, [scrollYProgress, totalProjects]);

  // Robust manual scroll navigation without page jump
  const scrollToProject = (targetIndex) => {
    if (!containerRef.current) return;
    playClick();
    const rect = containerRef.current.getBoundingClientRect();
    const currentScrollY =
      window.pageYOffset || document.documentElement.scrollTop;
    const containerAbsoluteTop = rect.top + currentScrollY;
    const targetScroll =
      containerAbsoluteTop +
      (targetIndex / (totalProjects - 1)) * scrollDistance;

    window.scrollTo({
      top: targetScroll,
      behavior: "smooth",
    });
  };

  const scrollMobileToProject = (targetIndex) => {
    if (!mobileTrackRef.current) return;
    playClick();
    const cards = mobileTrackRef.current.children;
    if (cards[targetIndex]) {
      cards[targetIndex].scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
      setActiveIndex(targetIndex);
    }
  };

  return (
    <div className="w-full">
      {/* Top Section Header */}
      <div className="container-main w-full mb-6 sm:mb-8">
        <motion.div
          variants={blurIn("up")}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.05 }}
        >
          <span className="section-label">Selected Work</span>
          <h2 className="section-title text-2xl lg:text-4xl font-bold">
            <ScrambleText text={projectsHeader.title} hover={true} />
          </h2>
          <p className="section-subtitle text-xs sm:text-sm max-w-xl text-fg-muted">
            {projectsHeader.description}
          </p>
        </motion.div>
      </div>

      {/* Desktop Horizontal Scroll-Driven Showcase */}
      <div
        ref={containerRef}
        className="hidden md:block relative w-full"
        style={{
          height:
            scrollDistance > 0 ? `calc(100vh + ${scrollDistance}px)` : "100vh",
        }}
      >
        <div className="sticky top-0 h-screen w-full flex flex-col justify-between py-8 lg:py-10 overflow-hidden">
          {/* Top Telemetry Header Bar */}
          <div className="container-main w-full">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-surface/[0.1] bg-surface/[0.03] text-xs font-mono text-fg-faint backdrop-blur-md">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-ping" />
                <span>Scroll to traverse</span>
                <span className="text-fg font-semibold">
                  ({String(activeIndex + 1).padStart(2, "0")} /{" "}
                  {String(totalProjects).padStart(2, "0")})
                </span>
              </div>

              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => scrollToProject(Math.max(0, activeIndex - 1))}
                  disabled={activeIndex === 0}
                  className="p-2 rounded-full border border-surface/[0.1] bg-surface/[0.04] text-fg hover:bg-surface/[0.1] disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                  aria-label="Previous project"
                >
                  <FiChevronLeft size={16} />
                </button>
                <button
                  onClick={() =>
                    scrollToProject(
                      Math.min(totalProjects - 1, activeIndex + 1)
                    )
                  }
                  disabled={activeIndex === totalProjects - 1}
                  className="p-2 rounded-full border border-surface/[0.1] bg-surface/[0.04] text-fg hover:bg-surface/[0.1] disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                  aria-label="Next project"
                >
                  <FiChevronRight size={16} />
                </button>
              </div>
            </div>
          </div>

          {/* Center Moving Horizontal Track with Parallax & Scaling */}
          <div className="w-full overflow-hidden my-auto py-4">
            <motion.div
              ref={trackRef}
              style={{ x: smoothX }}
              className="flex items-center gap-10 lg:gap-14 pl-8 sm:pl-16 lg:pl-24 pr-16 will-change-transform"
            >
              {projects.data.map((project, index) => (
                <DesktopProjectCard
                  key={project.name}
                  project={project}
                  index={index}
                  total={totalProjects}
                  scrollYProgress={scrollYProgress}
                />
              ))}
            </motion.div>
          </div>

          {/* Bottom Progress Bar & Numbered Markers */}
          <div className="container-main w-full">
            <div className="flex items-center justify-between gap-6 pt-3 border-t border-surface/[0.08]">
              {/* Progress Line */}
              <div className="flex-1 h-[2px] bg-surface/[0.08] rounded-full overflow-hidden">
                <motion.div
                  style={{ scaleX, transformOrigin: "0%" }}
                  className="h-full bg-fg shadow-[0_0_8px_rgba(var(--color-surface)/0.5)]"
                />
              </div>

              {/* Numbered Jump Markers */}
              <div className="flex items-center gap-2">
                {projects.data.map((p, i) => (
                  <button
                    key={p.name}
                    onClick={() => scrollToProject(i)}
                    className={`px-2.5 py-0.5 rounded text-[11px] font-mono transition-all duration-300 ${
                      i === activeIndex
                        ? "bg-surface/[0.12] text-fg border border-surface/[0.2] font-bold"
                        : "text-fg-faint hover:text-fg"
                    }`}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile / Tablet Horizontal Swipe Carousel */}
      <div className="md:hidden flex flex-col gap-6">
        {/* Mobile Swipeable Row */}
        <div
          ref={mobileTrackRef}
          className="flex items-stretch gap-4 px-6 overflow-x-auto snap-x snap-mandatory scrollbar-none pb-4"
        >
          {projects.data.map((project, index) => (
            <MobileProjectCard
              key={project.name}
              project={project}
              index={index}
              total={totalProjects}
            />
          ))}
        </div>

        {/* Mobile Indicator Dots & Jump Markers */}
        <div className="container-main flex items-center justify-between gap-4">
          <span className="text-[11px] font-mono text-fg-faint">
            Swipe to explore ({totalProjects})
          </span>
          <div className="flex items-center gap-1.5">
            {projects.data.map((p, i) => (
              <button
                key={p.name}
                onClick={() => scrollMobileToProject(i)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  i === activeIndex ? "w-6 bg-fg" : "bg-surface/[0.2]"
                }`}
                aria-label={`Jump to project ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Projects;
